<template>
  <!--
    Bill tab — pulls every bill in the session via
    GET /v1/services/{token}/bills on mount and on manual refresh.

    A session can hold multiple bills (e.g. first bill paid, customer
    ordered again → new bill opened). Newest is at the top per the
    server's id-desc ordering.

    Money is server-of-record: every total/breakdown shown here is
    derived from the per-bill fields the API sent (sub_total, discount,
    service_charge_amount, vat_rate, vatable_total, shipping_*). We
    only fold them into a friendly display — no rate is hard-coded.

    Customers don't settle payments through this UI; the "Request Bill"
    button just PUTs to /bills/request — it stamps session.bill_requested_at
    so staff at the POS can see the table is waiting. Split-bill UX was
    removed from the customer side intentionally; staff handles splits
    at the POS where the actual payment is captured.
  -->
  <div class="px-3 pt-3">

    <!--
      Top summary card. During the very first load (no bills cached yet)
      the dynamic values are swapped for inline shimmer bars instead of
      showing "Walk-in / ฿0.00" placeholders — that flash of fake data
      was the main cause of the "load → dump" feeling. Each value swap
      uses a tiny fade transition so the real number eases in rather
      than snapping in.
    -->
    <div class="card border-0 shadow-sm rounded-3 mb-3 bg-theme text-white">
      <div class="card-body p-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="small opacity-75">
            <!-- Skeleton → real value swaps instantly (no transition) so
                 the header doesn't visibly lag behind the network response.
                 The fade was adding ~240ms of perceived wait on top of the
                 fetch itself. -->
            <span v-if="initialLoading" class="skeleton-shimmer-light rounded d-inline-block align-middle" style="height: 12px; width: 110px;" />
            <span v-else class="d-inline-flex align-items-center flex-wrap" style="gap: 0.5rem;">
              <span v-if="sessionNoDisplay" class="fw-bold">
                #{{ sessionNoDisplay }}
              </span>
              <span v-if="session.table_name">
                <i class="fa-solid fa-chair me-1" />{{ session.table_name }}
              </span>
              <span>
                <i class="fa-solid fa-user me-1" />{{ session.customer_name || $t('Walk-in') }}
              </span>
              <span v-if="session.customer_count">
                <i class="fa-solid fa-user-group me-1" />{{ session.customer_count }}
              </span>
            </span>
          </div>
          <button
            class="btn btn-sm btn-light rounded-pill px-3 fw-bold"
            :disabled="loading"
            @click="fetchData(false)"
          >
            <i :class="loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'" />
            <span class="ms-1 d-none d-sm-inline">{{ $t('Refresh') }}</span>
          </button>
        </div>
        <div class="d-flex justify-content-between align-items-end">
          <div class="flex-grow-1">
            <div class="small opacity-75">{{ $t('Outstanding') }}</div>
            <div v-if="initialLoading" class="skeleton-shimmer-light rounded mt-1" style="height: 28px; width: 140px;" />
            <div v-else class="fs-3 fw-bold">{{ $storeIndex.formatThaiCurrency(outstandingTotal) }}</div>
          </div>
          <div v-if="initialLoading" class="text-end small">
            <div class="opacity-75">&nbsp;</div>
            <div class="skeleton-shimmer-light rounded mt-1" style="height: 16px; width: 80px;" />
          </div>
          <div v-else-if="paidTotal > 0" class="text-end small">
            <div class="opacity-75">{{ $t('Already paid') }}</div>
            <div class="fs-6 fw-bold">{{ $storeIndex.formatThaiCurrency(paidTotal) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loyalty (read-only) — โชว์เมื่อระบบเปิด + โต๊ะนี้ผูกสมาชิก (staff ผูกที่ POS) -->
    <div v-if="loyalty.enabled && loyalty.has_customer" class="card border-0 shadow-sm rounded-3 mb-3">
      <div class="card-body p-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fw-bold"><i class="fa-solid fa-star text-warning me-2" />{{ $t('_loyalty.My Points') }}</span>
          <span v-if="loyalty.customer_name" class="small text-muted">
            <i class="fa-solid fa-user me-1" />{{ loyalty.customer_name }}
          </span>
        </div>
        <div
          v-for="b in loyalty.balances"
          :key="b.program_id"
          class="d-flex justify-content-between"
          style="font-size: 0.85rem;"
        >
          <span class="text-muted">{{ b.program_name }}</span>
          <span class="fw-semibold">{{ formatPoints(b.balance) }} {{ $t('pts') }}</span>
        </div>
        <div v-if="!loyalty.balances.length" class="text-muted" style="font-size: 0.85rem;">
          {{ $t('No points yet') }}
        </div>
        <div class="d-flex justify-content-between border-top mt-2 pt-2" style="font-size: 0.85rem;">
          <span class="text-success"><i class="fa-solid fa-arrow-up me-1" />{{ $t('Points to earn') }}</span>
          <span class="fw-bold text-success">+{{ formatPoints(loyalty.earn_preview) }} {{ $t('pts') }}</span>
        </div>

        <!-- ของรางวัลที่แลกได้ (ดูเท่านั้น — แจ้งพนักงานเพื่อแลก) -->
        <div v-if="loyalty.rewards_affordable.length" class="border-top mt-2 pt-2">
          <div class="text-muted mb-2" style="font-size: 0.78rem;">
            <i class="fa-solid fa-gift me-1 text-theme" />{{ $t('_loyalty.Rewards you can redeem') }}
          </div>
          <div
            v-for="rw in loyalty.rewards_affordable"
            :key="rw.id"
            class="d-flex align-items-center gap-2 bg-light rounded-3 p-2 mb-1"
          >
            <div
              class="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
              :class="rw.reward_type === 'free_product' ? 'bg-warning-subtle text-warning' : 'bg-info-subtle text-info'"
              style="width: 30px; height: 30px;"
            >
              <i :class="rw.reward_type === 'free_product' ? 'fa-solid fa-gift' : 'fa-solid fa-tag'" style="font-size: 0.75rem;" />
            </div>
            <div class="flex-grow-1 min-w-0">
              <div class="fw-semibold text-truncate" style="font-size: 0.8rem;">{{ $t(rw.name) }}</div>
              <div class="text-muted" style="font-size: 0.7rem;">
                <i class="fa-solid fa-star text-warning me-1" />{{ formatPoints(rw.point_cost) }} {{ $t('pts') }}
                <span v-if="rw.reward_in_stock === false" class="badge bg-secondary ms-1">{{ $t('Out of stock') }}</span>
              </div>
            </div>
          </div>
          <div class="text-muted text-center" style="font-size: 0.7rem;">
            <i class="fa-solid fa-circle-info me-1" />{{ $t('_loyalty.Ask staff to redeem') }}
          </div>
        </div>
      </div>
    </div>

    <!-- โปรซื้อครบ: ของที่ส่งครัวแล้วถึงเงื่อนไข → ลูกค้ากดรับเลยที่นี่ ไม่ต้องสั่งใหม่ -->
    <!-- :initial-data = promotion ที่มากับ response บิล · auto-fetch=false → panel ไม่ fetch เอง (parent คุม) -->
    <service-promotion-panel :initial-data="promotion" :auto-fetch="false" @changed="fetchData" />

    <!-- Loading skeleton on first paint — shaped to mirror a real bill card
         (header row, status pill, two item lines, totals block, toggle) so
         when the real cards take over there's no layout shift. -->
    <div v-if="initialLoading" class="d-flex flex-column gap-2">
      <div
        v-for="i in 2" :key="'bill-sk-' + i"
        class="card border-0 shadow-sm rounded-3"
      >
        <div class="card-body p-3">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div class="flex-grow-1">
              <div class="skeleton-shimmer rounded mb-2" style="height: 16px; width: 45%;" />
              <div class="skeleton-shimmer rounded" style="height: 10px; width: 60%;" />
            </div>
            <div class="skeleton-shimmer rounded-pill" style="height: 20px; width: 70px;" />
          </div>
          <div class="border-top pt-2 mt-2">
            <div v-for="line in 2" :key="'bsl-' + i + '-' + line" class="d-flex justify-content-between py-1">
              <div class="flex-grow-1 pe-2">
                <div class="skeleton-shimmer rounded mb-1" style="height: 12px; width: 55%;" />
                <div class="skeleton-shimmer rounded" style="height: 10px; width: 35%;" />
              </div>
              <div class="skeleton-shimmer rounded" style="height: 14px; width: 50px;" />
            </div>
          </div>
          <div class="border-top pt-2 mt-2 d-flex justify-content-between">
            <div class="skeleton-shimmer rounded" style="height: 16px; width: 60px;" />
            <div class="skeleton-shimmer rounded" style="height: 18px; width: 90px;" />
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center text-muted py-5">
      <i class="fa-solid fa-circle-exclamation fa-3x text-danger mb-3 opacity-75" />
      <p class="fw-bold mb-1">{{ $t('Failed to load bill') }}</p>
      <p class="small mb-3">{{ error }}</p>
      <button class="btn btn-theme text-white rounded-pill px-4" @click="fetchData(false)">
        <i class="fa-solid fa-rotate me-2" />{{ $t('Try again') }}
      </button>
    </div>

    <!-- Empty (session active but nothing on the tab yet) -->
    <div v-else-if="bills.length === 0" class="text-center text-muted py-5">
      <i class="fa-solid fa-file-invoice-dollar fa-3x mb-3 opacity-50" />
      <p class="fw-bold">{{ $t('No bill yet') }}</p>
      <p class="small">{{ $t('Your bill appears here once you order') }}</p>
      <button class="btn btn-outline-theme rounded-pill px-4" @click="$emit('switch-tab', 'menu')">
        <i class="fa-solid fa-utensils me-2" />{{ $t('Open Menu') }}
      </button>
    </div>

    <!-- Bill cards (newest first per server) — wrapped in a transition-group
         with per-index stagger so cards ease in one after another instead
         of popping in all at once. `appear` makes the animation play on
         the first render after data arrives. -->
    <transition-group v-else name="list-stagger" tag="div" appear>
      <div
        v-for="(bill, idx) in bills"
        :key="'bill-' + bill.id"
        class="card border-0 shadow-sm rounded-3 mb-3"
        :class="{ 'opacity-75': bill.payment_status === 'paid' }"
        :style="{ '--enter-delay': (idx * 25) + 'ms' }"
      >
        <div class="card-body p-3">

          <!-- Header: bill no + status badge -->
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div class="fw-bold">
                <i class="fa-solid fa-file-invoice me-1 text-theme" />
                {{ bill.bill_no || ('#' + bill.id) }}
              </div>
              <div class="text-muted" style="font-size: 0.75rem;">
                <i class="fa-regular fa-clock me-1" />
                {{ formatDateTime(bill.created_at) }}
                <span v-if="bill.created_by" class="ms-2">
                  · {{ bill.created_by }}
                </span>
              </div>
            </div>
            <span class="badge" :class="statusBadgeClass(bill.payment_status)">
              <i :class="statusIcon(bill.payment_status)" class="me-1" />
              {{ $t(statusLabel(bill.payment_status)) }}
            </span>
          </div>

          <!-- Items list (collapsible per bill) -->
          <div
            v-if="expanded[bill.id] !== false"
            class="border-top pt-2 mt-2"
          >
            <div
              v-for="item in bill.items"
              :key="'bi-' + item.id"
              class="py-2"
              :class="{ 'opacity-50': toInt(item.cancelled_quantity) >= toInt(item.quantity) }"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="me-2">
                  <div class="fw-bold small">{{ $ln(item) }}</div>
                  <div class="text-muted" style="font-size: 0.72rem;">
                    x{{ toInt(item.quantity) }} · {{ $storeIndex.formatThaiCurrency(item.price) }}
                    <span v-if="item.tax_status === 'Exempt'" class="badge bg-light text-muted border ms-1" style="font-size: 0.6rem;">
                      {{ $t('Tax exempt') }}
                    </span>
                  </div>
                  <!-- Modifier options inline -->
                  <div v-if="item.options && item.options.length" class="ms-1">
                    <div
                      v-for="(opt, oi) in item.options"
                      :key="'opt-' + item.id + '-' + oi"
                      class="text-muted"
                      style="font-size: 0.7rem; line-height: 1.6;"
                    >
                      <i class="fa-solid fa-circle me-1" style="font-size: 0.25rem;" />
                      {{ $t(opt.name) }}
                      <span v-if="parseFloat(opt.price) > 0" class="text-theme">
                        +{{ $storeIndex.formatThaiCurrency(opt.price) }}
                      </span>
                    </div>
                  </div>
                  <!-- item_discount promo chip: บอกชัดว่าได้ราคาพิเศษกี่ชิ้น + ลดเท่าไร -->
                  <div v-if="toInt(item.item_discount_qty) > 0 && parseFloat(item.discount) > 0" class="mt-1">
                    <span class="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style="font-size: 0.68rem;">
                      <i class="fa-solid fa-tags me-1" />
                      {{ $t('_promotion.Promo price') }}: {{ toInt(item.item_discount_qty) }} {{ $t('_promotion.pcs') }}
                      (-{{ $storeIndex.formatThaiCurrency(item.discount) }})
                    </span>
                  </div>
                  <div v-if="item.remark" class="mt-1">
                    <span class="badge bg-light text-dark border" style="font-size: 0.68rem;">
                      <i class="fa-solid fa-comment-dots me-1 text-theme" />{{ item.remark }}
                    </span>
                  </div>
                  <!-- Partial-cancel chip -->
                  <div v-if="toInt(item.cancelled_quantity) > 0" class="mt-1">
                    <span class="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style="font-size: 0.68rem;">
                      <i class="fa-solid fa-ban me-1" />
                      {{ $t('Cancelled') }}: {{ toInt(item.cancelled_quantity) }}
                      <span v-if="parseFloat(item.cancelled_amount) > 0">
                        (-{{ $storeIndex.formatThaiCurrency(item.cancelled_amount) }})
                      </span>
                    </span>
                  </div>
                </div>
                <div class="text-end fw-bold">
                  <div v-if="toInt(item.item_discount_qty) > 0 && parseFloat(item.discount) > 0"
                       class="text-muted text-decoration-line-through fw-normal" style="font-size: 0.72rem;">
                    {{ $storeIndex.formatThaiCurrency(item.subtotal) }}
                  </div>
                  {{ $storeIndex.formatThaiCurrency(item.total) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Breakdown (server-provided figures only) -->
          <div class="border-top pt-2 mt-2 small">
            <div class="d-flex justify-content-between text-muted">
              <span>{{ $t('Subtotal') }}</span>
              <span>{{ $storeIndex.formatThaiCurrency(bill.sub_total) }}</span>
            </div>

            <!-- ส่วนลดโปรโมชัน (item_discount รายบรรทัด) — รวมจาก items ที่ส่งมา -->
            <div v-if="billPromoDiscount(bill) > 0" class="d-flex justify-content-between text-success">
              <span>{{ $t('_promotion.Promotion discount') }}</span>
              <span>- {{ $storeIndex.formatThaiCurrency(billPromoDiscount(bill)) }}</span>
            </div>

            <div v-if="parseFloat(bill.discount) > 0" class="d-flex justify-content-between text-danger">
              <span>
                {{ $t('Discount') }}
                <span v-if="bill.discount_reason" class="text-muted">({{ bill.discount_reason }})</span>
              </span>
              <span>- {{ $storeIndex.formatThaiCurrency(bill.discount) }}</span>
            </div>

            <div v-if="parseFloat(bill.service_charge_amount) > 0" class="d-flex justify-content-between text-muted">
              <span>{{ $t('Service charge') }} ({{ formatRate(bill.service_charge_rate) }}%)</span>
              <span>+ {{ $storeIndex.formatThaiCurrency(bill.service_charge_amount) }}</span>
            </div>

            <!-- VAT line: "(included)" suffix when tax_policy === Included
                 because it doesn't add on top of the grand total. -->
            <div v-if="vatAmount(bill) > 0" class="d-flex justify-content-between text-muted">
              <span>
                {{ $t('VAT') }} ({{ formatRate(bill.vat_rate) }}%)
                <span v-if="bill.tax_policy === 'Included'" class="text-muted">({{ $t('included') }})</span>
              </span>
              <span>{{ bill.tax_policy === 'Excluded' ? '+ ' : '' }}{{ $storeIndex.formatThaiCurrency(vatAmount(bill)) }}</span>
            </div>

            <div v-if="parseFloat(bill.shipping_price) > 0" class="d-flex justify-content-between text-muted">
              <span>{{ $t('Shipping') }}</span>
              <span>+ {{ $storeIndex.formatThaiCurrency(bill.shipping_price) }}</span>
            </div>

            <div class="d-flex justify-content-between fw-bold mt-2 pt-2 border-top">
              <span>{{ $t('Total') }}</span>
              <span class="text-theme fs-6">{{ $storeIndex.formatThaiCurrency(billGrandTotal(bill)) }}</span>
            </div>
          </div>

          <!-- Toggle items visibility -->
          <button
            class="btn btn-sm btn-link text-decoration-none w-100 mt-1 p-0 small"
            @click="toggleExpanded(bill.id)"
          >
            <i :class="expanded[bill.id] === false ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'" class="me-1" />
            {{ expanded[bill.id] === false ? $t('Show items') : $t('Hide items') }}
            <span class="text-muted">({{ bill.items?.length || 0 }})</span>
          </button>
        </div>
      </div>
    </transition-group>

    <!-- Request Bill button — single action now that split moved staff-side -->
    <div v-if="!loading && openBills.length > 0" class="mb-3">
      <button
        class="btn w-100 py-3 fw-bold rounded-3"
        :class="session.is_bill_requested ? 'btn-secondary text-white' : 'btn-theme text-white'"
        :disabled="session.is_bill_requested || requestingBill"
        @click="onRequestBill"
      >
        <i v-if="requestingBill" class="fa-solid fa-spinner fa-spin me-2" />
        <i v-else-if="session.is_bill_requested" class="fa-solid fa-check me-2" />
        <i v-else class="fa-solid fa-file-invoice-dollar me-2" />

        <template v-if="session.is_bill_requested">
          {{ $t('Bill requested') }} · {{ formatTime(session.bill_requested_at) }}
        </template>
        <template v-else>
          {{ $t('Request Bill') }}
        </template>
      </button>

      <!-- Re-request hint + button. Customer-facing copy frames it as a
           gentle nudge; the actual PUT happens whenever the cooldown
           has elapsed since the last bill_requested_at. -->
      <div v-if="session.is_bill_requested" class="text-center mt-2">
        <small class="text-muted d-block mb-1">
          <i class="fa-solid fa-circle-info me-1" />
          {{ $t('Staff has been notified — they will be with you shortly.') }}
        </small>
        <button
          v-if="canRequestAgain"
          class="btn btn-sm btn-outline-theme rounded-pill px-3"
          :disabled="requestingBill"
          @click="onRequestBill"
        >
          <i v-if="requestingBill" class="fa-solid fa-spinner fa-spin me-1" />
          <i v-else class="fa-solid fa-bell me-1" />
          {{ $t('Call again') }}
        </button>
        <small v-else class="text-muted d-block">
          {{ $t('You can call again in') }} {{ cooldownLeftLabel }}
        </small>
      </div>
    </div>

    <!-- "All paid" closing line -->
    <div
      v-if="!loading && bills.length > 0 && allBillsPaid"
      class="alert alert-success border-0 rounded-3 text-center"
    >
      <i class="fa-solid fa-circle-check fa-2x text-success mb-2" />
      <p class="fw-bold mb-1">{{ $t('All bills settled') }}</p>
      <p class="small mb-0">{{ $t('Thank you for dining with us') + '!' }}</p>
    </div>

  </div>
</template>

<script>
import { ServiceBills, isServiceSessionInvalid } from '@/services/endpoint/services.service';
import ServicePromotionPanel from './ServicePromotionPanel.vue';

// Customer can re-tap "Call again" after this much time has passed
// since the previous bill_requested_at. Keeps the API from being
// hammered if the customer is impatient.
const RE_REQUEST_COOLDOWN_MS = 5 * 60 * 1000;

export default {
  name: 'TabBill',
  components: { ServicePromotionPanel },
  emits: ['notify', 'switch-tab', 'rewards', 'ready', 'session-invalid'],
  data() {
    return {
      loading: true,
      error: null,
      bills: [],
      // แต้มสะสม (read-only) ของสมาชิกที่ผูกกับโต๊ะนี้
      loyalty: { enabled: false, has_customer: false, customer_name: '', balances: [], total_balance: 0, earn_preview: 0, rewards_affordable: [] },
      // โปรซื้อครบ — มากับ response บิล ส่งให้ ServicePromotionPanel ใช้ (ไม่ fetch แยก)
      promotion: null,
      session: {
        id: 0,
        token: '',
        type: '',
        state: '',
        customer_count: 0,
        table_id: 0,
        table_name: '',
        customer_name: '',
        session_no: null,
        bill_requested_at: null,
        is_bill_requested: false,
        created_at: '',
      },
      // Per-bill items expansion state — keyed by bill id. Defaults to
      // "expanded"; entries become false when the user collapses them.
      expanded: {},

      // Request-bill flow
      requestingBill: false,
      // Bumped by a 30s interval so the cooldown computed re-evaluates.
      // Without this, canRequestAgain wouldn't flip just because wall-clock
      // time advanced — Vue has no reason to re-render.
      _nowTick: 0,
      _tickTimer: null,
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    /** Bills not yet fully settled — what the customer still owes on. */
    openBills() {
      return this.bills.filter(b => b.payment_status !== 'paid');
    },
    paidBills() {
      return this.bills.filter(b => b.payment_status === 'paid');
    },
    outstandingTotal() {
      return this.openBills.reduce((sum, b) => sum + this.billGrandTotal(b), 0);
    },
    paidTotal() {
      return this.paidBills.reduce((sum, b) => sum + this.billGrandTotal(b), 0);
    },
    allBillsPaid() {
      return this.bills.length > 0 && this.bills.every(b => b.payment_status === 'paid');
    },
    /**
     * First-paint loading: no bills cached yet. We discriminate from
     * "manual refresh while already showing data" so the refresh button's
     * spinner doesn't replace the whole UI with skeletons.
     */
    initialLoading() {
      return this.loading && this.bills.length === 0;
    },
    sessionNoDisplay() {
      if (!this.session.session_no) return '';
      return String(this.session.session_no).padStart(4, '0');
    },

    /**
     * Milliseconds since the last bill_requested_at. `_nowTick` is read
     * here so the value updates whenever the heartbeat fires.
     */
    msSinceRequest() {
      void this._nowTick;
      if (!this.session.is_bill_requested || !this.session.bill_requested_at) return 0;
      return Date.now() - new Date(this.session.bill_requested_at).getTime();
    },
    canRequestAgain() {
      if (!this.session.is_bill_requested) return false;
      return this.msSinceRequest >= RE_REQUEST_COOLDOWN_MS;
    },
    cooldownLeftLabel() {
      const remaining = Math.max(0, RE_REQUEST_COOLDOWN_MS - this.msSinceRequest);
      const minutes = Math.ceil(remaining / 60000);
      return minutes + ' ' + this.$t('min');
    },
  },
  methods: {
    formatPoints(value) {
      return (parseInt(value) || 0).toLocaleString();
    },
    // แต้มสะสม read-only — map จาก loyalty ที่มากับ response บิล (ไม่ยิง /loyalty แยก)
    applyLoyalty(d) {
      const x = d || {};
      this.loyalty = {
        enabled: !!x.enabled,
        has_customer: !!x.has_customer,
        customer_name: x.customer_name || '',
        balances: x.balances || [],
        total_balance: x.total_balance || 0,
        earn_preview: x.earn_preview || 0,
        rewards_affordable: x.rewards_affordable || [],
      };
    },
    // --- Networking ------------------------------------------------------
    /**
     * @param {boolean} silent — when true, keep existing UI visible while
     *   refetching (used by the optimistic-update reconcile path). The
     *   manual refresh button passes false so first-load skeleton + an
     *   error toast still appear.
     */
    async fetchData(silent = false) {
      if (!silent) this.loading = true;
      this.error = null;
      let sessionInvalid = false;
      try {
        const res = await ServiceBills.list(this.token);
        const body = res.data ?? {};
        this.bills = Array.isArray(body.data) ? body.data : [];
        this.session = { ...this.session, ...(body.session ?? {}) };
        // promotion + loyalty มากับ response บิลแล้ว (ยิงเส้นเดียว) — ไม่เรียก /loyalty /promotion แยก
        this.applyLoyalty(body.loyalty);
        this.promotion = body.promotion || null;
        // relay ขึ้น parent → อัปเดต badge แท็บบิล + CTA (แทน refreshRewards หลัง send)
        this.$emit('rewards', { promotion: body.promotion ?? null, loyalty: body.loyalty ?? null });
      } catch (err) {
        if (isServiceSessionInvalid(err)) {
          sessionInvalid = true;
          this.$emit('session-invalid');
          return;
        }
        const msg = err?.response?.data?.message || err?.message || this.$t('Network error');
        this.error = msg;
        if (!silent && err?.response?.data?.message) {
          this.$emit('notify', { message: msg, variant: 'error', icon: 'fa-solid fa-triangle-exclamation' });
        }
      } finally {
        this.loading = false;
        if (!sessionInvalid) this.$emit('ready');
      }
    },

    /**
     * Fire PUT /bills/request with an optimistic UI update.
     *
     * - We stamp the local session immediately so the customer sees the
     *   button flip without waiting for the round trip.
     * - On success, the server's `data` block authoritatively overwrites
     *   our optimistic timestamp (could differ by a couple of seconds).
     * - On failure, we revert to the previous snapshot AND surface the
     *   API message verbatim — customer can retry from the same button.
     */
    async onRequestBill() {
      if (this.requestingBill) return;

      // Snapshot for revert
      const prev = {
        is_bill_requested: this.session.is_bill_requested,
        bill_requested_at: this.session.bill_requested_at,
      };

      this.requestingBill = true;
      this.session = {
        ...this.session,
        is_bill_requested: true,
        bill_requested_at: new Date().toISOString(),
      };

      try {
        const res = await ServiceBills.request(this.token);
        const data = res.data?.data ?? {};
        // Trust the server's timestamp (clocks may drift a few seconds).
        this.session = { ...this.session, ...data };
        this.$emit('notify', {
          message: this.$t('Staff has been notified for your bill'),
          variant: 'success',
          icon: 'fa-solid fa-file-invoice-dollar',
        });
      } catch (err) {
        // Roll back to whatever we had before — keeps the button live.
        this.session = { ...this.session, ...prev };
        if (isServiceSessionInvalid(err)) {
          this.$emit('session-invalid');
          return;
        }
        this.$emit('notify', {
          message: err?.response?.data?.message || this.$t('Failed to request bill'),
          variant: 'error',
          icon: 'fa-solid fa-triangle-exclamation',
        });
      } finally {
        this.requestingBill = false;
      }
    },

    // --- Bill math (display only) ---------------------------------------
    toInt(v) {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : 0;
    },
    /**
     * VAT amount for display. For Excluded VAT the value adds on top of
     * the grand total; for Included VAT it's back-calculated from the
     * already-vat-inclusive subtotal so the customer can see what portion
     * of what they're paying is tax (it does NOT add to the grand total).
     */
    vatAmount(bill) {
      const vatable = parseFloat(bill.vatable_total || 0);
      const rate = parseFloat(bill.vat_rate || 0);
      if (!vatable || !rate) return 0;
      return bill.tax_policy === 'Included'
        ? (vatable * rate) / (100 + rate)
        : (vatable * rate) / 100;
    },
    // ส่วนลดโปรโมชัน item_discount รวมทั้งบิล = ผลรวม discount ของบรรทัดที่ได้โปร (BE ส่ง discount + item_discount_qty มาแล้ว)
    billPromoDiscount(bill) {
      return (bill.items || []).reduce(
        (s, it) => (parseInt(it.item_discount_qty) > 0 ? s + parseFloat(it.discount || 0) : s),
        0
      );
    },
    billGrandTotal(bill) {
      const sub = parseFloat(bill.sub_total || 0);
      const promo = this.billPromoDiscount(bill);
      const disc = parseFloat(bill.discount || 0);
      const sc = parseFloat(bill.service_charge_amount || 0);
      const ship = parseFloat(bill.shipping_price || 0);
      // Excluded VAT layers on top; Included VAT is already inside sub_total.
      const vat = bill.tax_policy === 'Excluded' ? this.vatAmount(bill) : 0;
      return sub - promo - disc + sc + vat + ship;
    },

    // --- Status badge / icon --------------------------------------------
    statusBadgeClass(status) {
      return ({
        unpaid:  'bg-warning text-dark',
        partial: 'bg-info text-dark',
        paid:    'bg-success',
      })[status] || 'bg-secondary';
    },
    statusIcon(status) {
      return ({
        unpaid:  'fa-solid fa-hourglass-half',
        partial: 'fa-solid fa-circle-half-stroke',
        paid:    'fa-solid fa-circle-check',
      })[status] || 'fa-solid fa-circle';
    },
    statusLabel(status) {
      return ({
        unpaid:  'Unpaid',
        partial: 'Partially paid',
        paid:    'Paid',
      })[status] || status;
    },

    // --- Misc helpers ----------------------------------------------------
    formatRate(rate) {
      // Server sends "10.00" — trim trailing zeros so the label reads "10%"
      // rather than "10.00%".
      const n = parseFloat(rate);
      return Number.isFinite(n) ? n.toString() : rate;
    },
    formatDateTime(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString([], {
          month: 'short', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        });
      } catch { return ''; }
    },
    formatTime(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch { return ''; }
    },
    toggleExpanded(billId) {
      // Default is "expanded" (undefined → treated as true). Flip to
      // explicit false on collapse, back to true on expand.
      const current = this.expanded[billId];
      this.expanded = { ...this.expanded, [billId]: current === false ? true : false };
    },
  },
  mounted() {
    this.fetchData();
    // Re-render once every 30s so the "Call again" cooldown countdown
    // ticks down without requiring user interaction.
    this._tickTimer = setInterval(() => { this._nowTick++; }, 30 * 1000);
  },
  beforeUnmount() {
    if (this._tickTimer) clearInterval(this._tickTimer);
  },
};
</script>

<style scoped>
/* Warm, brand-tinted shimmer — same recipe across all customer service
   tabs so the loading visuals stay consistent and on-brand. */
.skeleton-shimmer {
  background: linear-gradient(100deg,
    #f4e6d8 0%,
    #fcefe4 42%,
    #fff9f3 50%,
    #fcefe4 58%,
    #f4e6d8 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.4s ease-in-out infinite;
  display: block;
}
/* Lighter shimmer for placeholders sitting on the orange theme header. */
.skeleton-shimmer-light {
  background: linear-gradient(90deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.35) 50%,
    rgba(255, 255, 255, 0.15) 100%);
  background-size: 200% 100%;
  animation: pos-skeleton-shimmer 1.4s ease-in-out infinite;
  display: block;
}
@keyframes pos-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/*
  Tiny opacity cross-fade for skeleton → real text. Kept short (~120ms
  each direction) so the perceived wait between data arriving and the
  real value showing stays under ~250ms total. Earlier values felt
  laggy; this is fast enough to feel responsive while still smoothing
  the swap visually.
*/
.fade-swap-enter-active,
.fade-swap-leave-active {
  transition: opacity 0.12s ease;
}
.fade-swap-enter-from,
.fade-swap-leave-to {
  opacity: 0;
}

/*
  Staggered list entrance for bill cards. `--enter-delay` is set inline
  per-row so the cards ripple in. Stagger is intentionally tight (~25ms
  apart, 0.2s per card) so the list feels alive without making the user
  wait perceptibly longer than a single fade.
*/
.list-stagger-enter-active {
  transition: opacity 0.2s ease-out, transform 0.2s ease-out;
  transition-delay: var(--enter-delay, 0ms);
}
.list-stagger-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
</style>
